/**
 * New run, and Refine run: one dialog. Client, problem, industry and country, optional notes or transcripts
 * (each file's text status shown live), an optional business opportunity (`idea.view`) and CRM reference. A refinement
 * opens with the run's inputs and starts a new run; the one it refines is kept.
 */

import { Play, WandSparkles } from 'lucide-react';

import { runInClientMax, runInCrmReferenceOneMax, runInProblemMax } from '@/api/generated/schemas';
import { useCountries, useIndustries } from '@/api/queries';
import type { AdvisorIdeaRef, AdvisorRun, TextStatus } from '@/api/types';
import { bannerError } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { AttachmentPicker } from '@/ui/Attachments';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { formatInt } from '@/ui/format';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { IdeaPicker } from './IdeaPicker';
import { useRunForm, type RunForm } from './useRunForm';

const TEXT_STATUS: Record<TextStatus, string> = {
  pending: 'Reading the text',
  ready: 'Text ready',
  unreadable: 'No readable text: the run leaves it out',
};

function ClientFields({ form }: { form: RunForm }) {
  const { can } = useSession();
  const industries = useIndustries({ enabled: can('industry.view') });
  const countries = useCountries();
  const active = (industries.data?.industries ?? []).filter((i) => i.status === 'approved' && i.is_active);
  return (
    <div className="grid grid-2">
      <div className="field">
        <label htmlFor="run-client">Client</label>
        <input id="run-client" type="text" required autoFocus maxLength={runInClientMax} value={form.client}
          placeholder="Client or prospect" {...fieldA11y('run-client', form.serverErrors.client)} onChange={(e) => form.setClient(e.target.value)} />
        <FieldError inputId="run-client" message={form.serverErrors.client} />
      </div>
      <div className="field">
        <label htmlFor="run-crm">CRM reference (optional)</label>
        <input id="run-crm" type="text" maxLength={runInCrmReferenceOneMax} value={form.crm}
          placeholder="Opportunity number" {...fieldA11y('run-crm', form.serverErrors.crm_reference)} onChange={(e) => form.setCrm(e.target.value)} />
        <FieldError inputId="run-crm" message={form.serverErrors.crm_reference} />
      </div>
      {can('industry.view') && (
        <div className="field">
          <label htmlFor="run-industry">Industry</label>
          <select id="run-industry" value={form.industryId} onChange={(e) => form.setIndustryId(e.target.value)}>
            <option value="">Not sure or cross-industry</option>
            {active.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
      )}
      <div className="field">
        <label htmlFor="run-country">Country</label>
        <select id="run-country" value={form.country} onChange={(e) => form.setCountry(e.target.value)}>
          <option value="">Not stated</option>
          {(countries.data ?? []).filter((c) => c.code !== 'GLOBAL').map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </div>
    </div>
  );
}

function ProblemField({ form }: { form: RunForm }) {
  return (
    <div className="field">
      <label htmlFor="run-problem">What problem did you see?</label>
      <textarea id="run-problem" rows={7} required maxLength={runInProblemMax} value={form.problem}
        {...fieldA11y('run-problem', form.serverErrors.problem, 'run-problem-count')}
        placeholder="The pain in the client's own words if you can: volumes, tools, deadlines, regulators."
        onChange={(e) => form.setProblem(e.target.value)} />
      <span id="run-problem-count" className="hint char-count">
        {formatInt(form.problem.length)} / {formatInt(runInProblemMax)} characters
      </span>
      <FieldError inputId="run-problem" message={form.serverErrors.problem} />
    </div>
  );
}

function FilesField({ form }: { form: RunForm }) {
  const { draft, statusOf } = form.files;
  return (
    <div className="field">
      <label htmlFor="run-files">Notes, transcript or RFP (optional)</label>
      <AttachmentPicker draft={draft} id="run-files" label="Attach files" layout="table" describedBy="run-files-note" />
      <span id="run-files-note" className="hint">
        The AI reads up to 30,000 characters of the problem and the files together. Files you do not use are removed after a day.
      </span>
      {draft.files.length > 0 && (
        <ul className="advisor-file-status" aria-label="Text of the attached files" aria-live="polite">
          {draft.files.map((file) => (
            <li key={file.id}>
              <span>{file.filename}</span> <span className="hint">{TEXT_STATUS[statusOf(file)]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** What the footer says beside Run: why it is disabled when it is, else what a run costs. */
function footerNote(form: RunForm): string {
  if (form.invalid) return 'Fill in the client and the problem to run.';
  if (form.files.reading) return 'Waiting for the files to be read.';
  return 'Uses AI budget. The run goes on in the background.';
}

function Footer({ form, onClose }: { form: RunForm; onClose: () => void }) {
  return (
    <div className="submit-actions">
      <span id="run-footer-note" className="hint">{footerNote(form)}</span>
      <span className="row">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <ActionButton
          state={buttonState(form.save)}
          icon={form.revising ? <WandSparkles aria-hidden="true" /> : <Play aria-hidden="true" />}
          label={form.revising ? 'Refine' : 'Run'}
          pendingLabel="Starting"
          doneLabel="Started"
          disabled={!form.canSubmit}
          describedBy={form.invalid ? 'run-footer-note' : undefined}
        />
      </span>
    </div>
  );
}

function IdeaField({ form, source }: { form: RunForm; source?: AdvisorRun }) {
  const canLink = useSession().can('idea.view');
  // A revision of a run linked to an opportunity the reader cannot open keeps the link as it is, unseen.
  if (!canLink || (source?.idea_linked && !source.idea)) return null;
  return (
    <div className="field">
      <label htmlFor="run-idea">Business opportunity (optional)</label>
      <IdeaPicker id="run-idea" value={form.linked} onChange={form.setLinked} />
      {form.linked && <span className="hint">The AI reads the opportunity's title, description and research summary.</span>}
    </div>
  );
}

export function NewRunDialog({
  source,
  idea = null,
  onClose,
  onStarted,
}: {
  /** The run to refine; without it the dialog starts a new one. */
  source?: AdvisorRun;
  idea?: AdvisorIdeaRef | null;
  onClose: () => void;
  onStarted: (run: AdvisorRun) => void;
}) {
  const form = useRunForm(source, idea, (run) => {
    onStarted(run);
    onClose();
  });
  return (
    <Modal
      title={form.revising ? 'Refine run' : 'New Advisor run'}
      subtitle={form.revising ? 'Change the inputs and run again. The earlier run is kept.' : 'The Advisor screens every published asset against the client need.'}
      size="wide"
      onClose={onClose}
      onSubmit={form.onSubmit}
      footer={<Footer form={form} onClose={onClose} />}
    >
      <div className="stack-md">
        <FormError error={bannerError(form.save.error, form.placed)} />
        <ClientFields form={form} />
        <ProblemField form={form} />
        <FilesField form={form} />
        <IdeaField form={form} source={source} />
      </div>
    </Modal>
  );
}
